<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useNuxtApp } from '#app'
import RateObject from '~/components/RateObject.vue'
import ReportRateModal from "@/components/ReportRateModal.vue";
// import EditRateModal from '~/components/EditRateModal.vue'
import { ratingsRepository } from '~/utils/ratingsRepository'
import { clientsRepository } from '~/utils/clientsRepository'


// Ratings state
const ratings = ref<any[]>([])
// const user = ref<any>(null)  // Initially null; replace with your proper User type if available
const nextPage = ref<number | null>(null)
const pageLoading = ref<boolean>(true)
const rates_count_text = ref<Number>(0)
const ratingsLoading = ref<boolean>(true)
const filterOrder = ref(null)
const filterSearchInput = ref(null)

// Get your custom API instance (provided by your Nuxt plugin)
const { $api } = useNuxtApp()
const route = useRoute();

// Instantiate your repositories with the custom API
const ratingRepo = ratingsRepository($api)
const clientRepo = clientsRepository()

const userId = computed(() => route.params.user_id as string);


const { data: user, error } = await useAsyncData(`place-${userId.value}`, () =>
  clientRepo.showById(userId.value), {
  lazy: false, // يجبر SSR على جلب البيانات عند تحميل الصفحة
}
);
const currentUrl = import.meta.client ? window.location.href : useRequestURL().href;
pageLoading.value = false

if (error.value) {
  navigateTo('/wrong', { redirectCode: 404 });

  console.error('useAsyncData Error:', error.value);
}

// /**
//  * Fetch client data.
//  */
// const fetchClient = async () => {

//   pageLoading.value = true
//   try {
//     // Assuming the current user id is available.
//     // If you already have a global user store, you can use that.
//     if (!user.value) {
//       // For demonstration, assign a dummy user id.
//       user.value = { id: 1 }
//     }
//     const data = await clientRepo.showById(route.params.user_id)
//     user.value = data
//   } catch (error) {
//     console.error("Error fetching client data:", error)
//   } finally {
//     pageLoading.value = false
//   }
// }

const slugify = (text: string): string =>
  text ? text.trim().replace(/\s+/g, '-').toLowerCase() : ''

if (user.value) {



  const ogTitle = `${user.value.name} | رفيو المنصة الأولى في التقييم و معرفة تجارب الآخرين و الاستفادة منها`


  useHead({
    link: [
      {
        rel: 'canonical',
        // Using window.location.href ensures the current URL is set as canonical.
        href: currentUrl
      }
    ],
    title: `${user.value.name}`,
    meta: [
      {
        name: 'title',
        content: ogTitle
      },
      { property: 'og:title', content: ogTitle },
      { property: 'twitter:title', content: ogTitle },
    ],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "الرئيسية",
              "item": "https://reviewapp.sa/"  // Adjust if you have a specific home route
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": user.value.name,
              "item": currentUrl
            }
          ]
        })
      }
    ]
  });
}

/**
 * Fetch ratings for the current user.
 * @param {number} page - The page number (default is 1).
 */
const fetchRatings = async (page: number = 1) => {
  ratingsLoading.value = true
  try {
    const response = await clientRepo.getClientRatings(
      userId.value,
      { 
        user_id: userId.value, 
        sort: filterOrder.value,
        review: filterSearchInput.value ?? null
      },
      page
    )
    const { data, appended_data, nextPage: np } = response
    rates_count_text.value = appended_data.rating_count ?? 0
    if (page === 1) {
      ratings.value = data
    } else {
      ratings.value.push(...data)
    }
    nextPage.value = np
  } catch (error) {
    console.error("Error fetching ratings:", error)
  } finally {
    ratingsLoading.value = false
  }
}

/**
 * Called when the user clicks the "Load More" button.
 */
const loadMoreRatings = async () => {
  if (nextPage.value) {
    await fetchRatings(nextPage.value)
  }
}

/**
 * Set a new filter order and re-fetch ratings.
 * @param {string} order - One of "top-rated", "most-rated", or "recent-rated".
 */
const setFilter = async (order) => {
  filterOrder.value = order
  ratings.value = [];
  await fetchRatings(1)
}


const {
          closeModal
      } = useBootstrapModal("filter_rate_modal");


      
/**
 * Handle the reset filter button.
 * Resets the filter to default values, clears the search input,
 * refreshes the reviews with default filter and closes the modal.
 */
const resetFilter = async () => {
  // Reset filter selections to default values (adjust as needed)
  filterOrder.value = null           // Reset to default value "latest"
  filterSearchInput.value = null      // Clear the search input

  // Reset the reviews list and load the default reviews.
  ratings.value = []
  await fetchRatings(1)

  // Optionally, you might want to close the modal here as well.
  closeModal()
}

/**
 * Handle form submission by setting the filter order.
 * The filtering logic could be extended to include search parameters
 * or additional business logic.
 */
const submitFilter = async (e: Event) => {
    ratingsLoading.value = true;
    ratings.value = [];

    await fetchRatings(1)
    
    ratingsLoading.value = false;

    closeModal()
}

const reportModalRef = ref<InstanceType<typeof ReportRateModal> | null>(null)

const handleReport = (ratingId: String) => {




  if (reportModalRef.value) {
    reportModalRef.value.setRatingId(ratingId)
    reportModalRef.value.openModal()
  }
}

onMounted(async () => {
  await fetchRatings(1)
});

</script>

<template>

  <NuxtLayout name="default" mainClass="inside_main">
    <div v-if="!pageLoading" class="user-profile inside">
      <div class="container">
        <div class="row">
          <!-- Left Column: User Profile Info -->
          <div class="col-md-4">
            <div class="user-profile-info">
              <div class="user_image">

                <NuxtImg :src="user?.thumbnail" :alt="user?.name" loading="lazy" :custom="true"
                  v-slot="{ src, isLoaded, imgAttrs }">
                  <!-- Show the actual image when loaded -->
                  <img v-if="isLoaded" v-bind="imgAttrs" :src="src">

                  <!-- Show a placeholder while loading -->
                  <img v-else :src="'/website/assets/img/avatar.png'">
                </NuxtImg>
                <!-- <img :src="user?.thumbnail || '/website/assets/img/avatar.png'" alt="User Avatar" /> -->
              </div>
              <div class="user_info">
                <h3>
                  
                  {{ user?.name }}
                </h3>
                <p>{{ user?.description }}</p>
                <div class="rates_counts">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20.303" height="19.493" viewBox="0 0 20.303 19.493">
  <path id="star-comment-alt-2" d="M11.141,24.285a1.714,1.714,0,0,1-1.016-.333,1.686,1.686,0,0,1-.591-1.866l1.589-5.1-4.028-3.27a1.685,1.685,0,0,1,1.09-2.975h5.081l1.792-4.823a1.693,1.693,0,0,1,3.178,0l1.792,4.823H25.11A1.7,1.7,0,0,1,26.7,11.85a1.73,1.73,0,0,1-.5,1.885L22.19,17l1.663,5.044a1.677,1.677,0,0,1-.573,1.866,1.7,1.7,0,0,1-1.959.074l-4.656-3.03-4.582,3.067a1.7,1.7,0,0,1-.942.277Z" transform="translate(-6.496 -4.81)" fill="#f7b32f"/>
</svg>

                  <!-- Uncomment and adjust the next line if you have a rates count text -->
                  <span>{{ rates_count_text }} تقييم</span>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Column: User Ratings -->
          <div class="col-md-8">
            <div class="row align-items-center">
              <div class="col-6">
                <div class="section_title">
                  <h1>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <g id="Group_1" data-name="Group 1" transform="translate(-820 -204)">
                          <g id="trend-up_linear" data-name="trend-up/linear" transform="translate(820 204)">
                            <g id="vuesax_linear_trend-up" data-name="vuesax/linear/trend-up">
                              <g id="trend-up">
                                <path id="Vector" d="M7,20h6c5,0,7-2,7-7V7c0-5-2-7-7-7H7C2,0,0,2,0,7v6C0,18,2,20,7,20Z" transform="translate(2 2)" fill="none" stroke="#292d32" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                                <path id="Vector-2" data-name="Vector" d="M0,0H24V24H0Z" fill="none" opacity="0"/>
                              </g>
                            </g>
                          </g>
                          <g id="star_linear" data-name="star/linear" transform="translate(825 209)">
                            <g id="vuesax_linear_star" data-name="vuesax/linear/star">
                              <g id="star">
                                <path id="Vector-3" data-name="Vector" d="M5.865.71l.88,1.746a1.086,1.086,0,0,0,.71.521l1.6.263c1.02.169,1.26.9.525,1.627L8.335,6.1a1.071,1.071,0,0,0-.26.9L8.43,8.516c.28,1.205-.365,1.671-1.44,1.041L5.5,8.68a1.089,1.089,0,0,0-.99,0l-1.5.878c-1.07.63-1.72.159-1.44-1.041l.355-1.522a1.071,1.071,0,0,0-.26-.9L.425,4.866c-.73-.724-.5-1.458.525-1.627l1.6-.263a1.088,1.088,0,0,0,.705-.521L4.13.71C4.61-.237,5.39-.237,5.865.71Z" transform="translate(2.001 2.078)" fill="none" stroke="#292d32" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4"/>
                                <path id="Vector-4" data-name="Vector" d="M0,0H14V14H0Z" fill="none" opacity="0"/>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>

                    <span>التقييمات</span>
                  </h1>
                </div>
              </div>
              <div class="col-6">
                <div class="more">
                  <div class="dropdown-filter-main">
                    <button class="section_subtitle dropdown-toggle"data-bs-toggle="modal"
                      data-bs-target="#filter_rate_modal"
                      aria-expanded="false">
                      <span>فلتر</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 22 20">
                        <g transform="translate(-1 -2)">
                          <path
                            d="M17,5a3,3,0,1,1,3,3,3,3,0,0,1-3-3ZM2,6H14a1,1,0,0,0,0-2H2A1,1,0,0,0,2,6ZM8,9a3,3,0,0,0-2.82,2H2a1,1,0,0,0,0,2H5.18A3,3,0,1,0,8,9Zm14,2H14a1,1,0,0,0,0,2h8a1,1,0,0,0,0-2ZM10,18H2a1,1,0,0,0,0,2h8a1,1,0,0,0,0-2Zm12,0H18.82a3,3,0,1,0,0,2H22a1,1,0,0,0,0-2Z"
                            fill="#292d32"></path>
                        </g>
                      </svg>
                    </button>
                    <!-- <ul class="dropdown-menu dropdown-menu-start" aria-labelledby="dropdownMenuButton">
                      <li>
                        <a class="dropdown-item user-filter" :class="{ active: filterOrder === '1' }"
                          @click="setFilter('1')">بحسب
                          الأعلى تقييماً</a>
                      </li>
                      <li>
                        <a class="dropdown-item user-filter" :class="{ active: filterOrder === '2' }"
                          @click="setFilter('2')">بحسب
                          الأكثر تقييماً</a>
                      </li>
                      <li>
                        <a class="dropdown-item user-filter" :class="{ active: filterOrder === '3' }"
                          @click="setFilter('3')">الأحدث تقييماً</a>
                      </li>
                    </ul> -->
                  </div>
                </div>
              </div>
            </div>
            <!-- Ratings List -->
            <div id="user_rates_cont" class="row places_container">
              <template v-if="ratings.length">
                  
                  <RateObject v-for="rate in ratings" :filterText="filterSearchInput" :key="rate.id" :rate="rate" :userShow="true" :placeShow="false"
                  :classShow="'col-md-12'" @open-report="handleReport" />
              </template>
              <template v-else-if="ratingsLoading">
                <!-- <div class="col-md-12">
                  <div class="item_rate no-rate has-spinner-link-dark loading">
                    <div class="item_content">
                      <span class="addRateLink">
                      </span>
                    </div>
                  </div>
                </div> -->
                <RateObjectPlaceholder v-for="n in 6" :key="n" :userShow="true" :placeShow="false" />
              </template>
              <template v-else>
                <div class="col-md-12">
                  <div class="item_rate no-rate">
                    <div class="item_content">
                      <p>لم تقم بأي تقييمات بعد</p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <!-- Load More Button -->
            <div v-if="nextPage" class="moreLoading">
              <button class="section_subtitle user-load-more has-spinner-link" @click="loadMoreRatings"
                :disabled="ratingsLoading">
                <div class="button_text">عرض المزيد</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <ReportRateModal ref="reportModalRef" dir="rtl" />

      <div class="modal bottomSheet userModels FilterRateModal" id="filter_rate_modal" tabindex="-1"
        aria-labelledby="filterModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <!-- Modal Header -->
            <div class="modal-header pb-0">

            </div>
            <!-- Modal Body -->
            <div class="modal-body pt-3">
              <form @submit.prevent="submitFilter">
                <div class="filter-rate-wrapper">
                  <div class="filter-head-title">
                    <h5 class="filter-title">فلترة التقييمات</h5>
                    <button @click="resetFilter" type="button" class="reset-filter">مسح الفلتر</button>
                  </div>
                  <div class="filter-body">
                    <div class="part">
                      <p class="filter_label">
                        ترتيب حسب
                      </p>
                      <div class="radio-input">
                        <label class="label">
                          <input type="radio" id="latest" v-model="filterOrder" name="value-radio" value="3">
                          <p class="text">الأحدث</p>
                        </label>

                        <label class="label">
                          <input type="radio" id="highest" v-model="filterOrder" name="value-radio" value="1">
                          <p class="text">الأعلى</p>
                        </label>

                        <label class="label">
                          <input type="radio" id="lowest" v-model="filterOrder" name="value-radio" value="4">
                          <p class="text">الأقل</p>
                        </label>

                        <label class="label">
                          <input type="radio" id="most-liked" v-model="filterOrder" name="value-radio" value="2">
                          <p class="text">الأكثر إعجاباً</p>
                        </label>
                      </div>
                    </div>
                    <div class="part">
                      <p class="filter_label">
                        تقييم يحتوي على كلمة
                      </p>

                      <div class="search_input">
                        <input class="searchInput" type="text" v-model="filterSearchInput" name="q" id="qM"
                          autocomplete="off" placeholder="بحث">
                        <button class="searchButton" type="button">
                          <svg id="vuesax_linear_search-normal" data-name="vuesax/linear/search-normal"
                            xmlns="http://www.w3.org/2000/svg" width="21.029" height="21.029"
                            viewBox="0 0 21.029 21.029">
                            <g id="search-normal">
                              <path id="Vector"
                                d="M16.648,8.324A8.324,8.324,0,1,1,8.324,0,8.324,8.324,0,0,1,16.648,8.324Z"
                                transform="translate(1.752 1.752)" fill="none" stroke="#d0d5dc" stroke-linecap="round"
                                stroke-linejoin="round" stroke-width="1.7" />
                              <path id="Vector-2" data-name="Vector" d="M1.752,1.752,0,0"
                                transform="translate(17.524 17.524)" fill="none" stroke="#d0d5dc" stroke-linecap="round"
                                stroke-linejoin="round" stroke-width="1.7" />
                              <path id="Vector-3" data-name="Vector" d="M0,0H21.029V21.029H0Z" fill="none"
                                opacity="0" />
                            </g>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button id="saveFilterButton" type="submit" class="filterSaveBtn has-spinner-link">
                      <div class="button_text">عرض</div>
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <!-- End Modal Body -->
          </div>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
